import { createSlice, isAnyOf } from '@reduxjs/toolkit'

import { getCurrencySymbol } from '@/common/utils'

import { organisationApi } from '../services/organisation'

const getPlanMetadataTypeReducer = (type) => {
    switch (type) {
        case 'number':
            return (acc, curr) => {
                const accVal = parseInt(acc)
                const currVal = parseInt(curr)
                // If both are invalid, return undefined
                if (isNaN(accVal) && isNaN(currVal)) return undefined
                // If only one is valid, return the valid one
                if (isNaN(accVal)) return currVal
                if (isNaN(currVal)) return accVal
                // Both valid, return maximum
                return Math.max(accVal, currVal)
            }
        case 'number_min':
            return (acc, curr) => {
                const accVal = parseFloat(acc)
                const currVal = parseFloat(curr)
                // If both are invalid, return undefined
                if (isNaN(accVal) && isNaN(currVal)) return undefined
                // If only one is valid, return the valid one
                if (isNaN(accVal)) return currVal
                if (isNaN(currVal)) return accVal
                // Both valid, return minimum
                return Math.min(accVal, currVal)
            }
        case 'string':
            // For string type, use first non-empty value
            return (acc, curr) => acc || curr
        default:
            // Boolean type - convert to boolean
            return (acc, curr) => acc || curr === 'true'
    }
}

const PLAN_METADATA_TYPES = {
    '3d': 'boolean',
    customer_pricing: 'boolean',
    customer_supplied: 'boolean',
    fixed_price_parts: 'boolean',
    material_consumption: 'boolean',
    materials_revamp: 'boolean',
    max_users: 'number',
    miscellaneous_items: 'boolean',
    part_library: 'boolean',
    payments_fee_internal: 'number_min',
    payments_fee: 'number_min',
    pdf: 'boolean',
    plan: 'string',
    quickbooks: 'boolean',
    shipping: 'boolean',
    tube: 'boolean',
    webstore: 'boolean',
    xero: 'boolean',
    zapier: 'boolean',
}

/**
 * Extracts metadata from subscription items supporting both Stripe pricing structures.
 *
 * OLD STRUCTURE (price-level metadata):
 *   - subscriptionItem.price.metadata contains all feature flags
 *   - subscriptionItem.price.metadata.max_users = '5'
 *
 * NEW STRUCTURE (product-level metadata):
 *   - subscriptionItem.product.metadata contains all feature flags
 *   - No max_users in metadata, use subscriptionItem.quantity instead
 *
 * @param {Array} subscriptions - Array of subscription objects
 * @returns {Object} Metadata map keyed by priceId or productId
 */
const extractSubscriptionMetadata = (subscriptions) => {
    if (!subscriptions) return {}

    return subscriptions
        .flatMap((subscription) => subscription.subscriptionItems || [])
        .reduce((metadataMap, subscriptionItem) => {
            // Priority 1: Try price metadata (OLD structure - backward compatibility)
            // Strip $type before counting keys, so price metadata holding only $type falls through to product metadata
            const { $type, ...priceMetadata } = subscriptionItem.price?.metadata ?? {}

            if (Object.keys(priceMetadata).length > 0) {
                metadataMap[subscriptionItem.priceId] = priceMetadata
            }
            // Priority 2: Try product metadata (NEW structure)
            else if (subscriptionItem.product?.metadata) {
                const { $type, ...metadataDto } = subscriptionItem.product.metadata
                // Only add if there's actual metadata beyond $type
                if (Object.keys(metadataDto).length > 0) {
                    metadataMap[subscriptionItem.productId] = metadataDto
                }
            }

            return metadataMap
        }, {})
}

/**
 * Calculates maximum allowed users from subscription data.
 *
 * BUSINESS RULES:
 * 1. Start with max_users from metadata if present (largest value across all subscriptions)
 * 2. Calculate largest quantity across all subscription items
 * 3. Return the MAXIMUM of both values (max_users from metadata AND quantities)
 * 4. Enforce minimum of 1 user (every organization needs at least one user)
 *
 * HYBRID SUBSCRIPTION LOGIC:
 * This dual-logic supports migration from price-level to product-level metadata
 * without requiring existing subscriptions to be migrated. When organizations have
 * both old (price-level with max_users) and new (product-level with quantity)
 * subscriptions, they benefit from the most generous user limit.
 *
 * EXAMPLE:
 * - Subscription A (old): max_users = '5' in price.metadata
 * - Subscription B (new): quantity = 20 in subscriptionItem
 * - Result: max(5, 20) = 20 users (customer gets the higher limit)
 *
 * @param {Array} subscriptions - Array of subscription objects
 * @param {Object} resolvedMetadata - Merged metadata from all subscriptions (max_users already resolved to largest value)
 * @returns {number} Maximum allowed users (minimum 1)
 */
const calculateMaxUsers = (subscriptions, resolvedMetadata) => {
    let maxUsers = 1

    if (!subscriptions || subscriptions.length === 0) {
        return maxUsers
    }

    // Extract max_users from metadata if present (already contains the largest value across all subscriptions)
    if (resolvedMetadata?.max_users !== undefined) {
        maxUsers = Math.max(1, parseInt(resolvedMetadata.max_users) || 1)
    }

    // Extract all quantities from subscription items
    const quantities = subscriptions
        .flatMap((subscription) => subscription.subscriptionItems || [])
        .map((subscriptionItem) => parseInt(subscriptionItem.quantity) || 1)

    // Return the maximum of max_users (from metadata) and all quantities
    // This ensures customers get the most generous limit when combining subscription types
    maxUsers = Math.max(maxUsers, ...quantities)

    return maxUsers
}

const initialState = {
    organisation: {},
    organisationId: undefined,
    lastPaymentStatus: undefined,
    paidFeatures: undefined,
    useImperialUnits: undefined,
    currencySymbol: undefined,
    currencyCode: undefined,
    isOnTrial: false,
    planMetadata: undefined,
    isOnFreePlan: false,
    showAccelerationSettings: false,
    defaultTaxRateId: undefined,
    hasActiveSubscription: false,
    useTubeV2: false,
    maxUsers: 1,
}

export const organisationSlice = createSlice({
    name: 'organisationSlice',
    initialState,
    reducers: {
        setOrganisation: (state, { payload }) => {
            if (!payload) return

            const isOnTrialValue = (payload?.trial && !payload?.trial.hasExpired) ?? false
            const isOnFreePlan = Boolean(payload?.freePlan)

            // Extract metadata from both old (price-level) and new (product-level) structures
            const metadata = extractSubscriptionMetadata(payload?.subscriptions)

            const resolvedMetadata = metadata
                ? Object.values(metadata).reduce((acc, currPlanMetadata) => {
                      return Object.keys(currPlanMetadata).reduce((innerAcc, metadataKey) => {
                          innerAcc[metadataKey] = getPlanMetadataTypeReducer(PLAN_METADATA_TYPES[metadataKey])(
                              innerAcc[metadataKey],
                              currPlanMetadata[metadataKey]
                          )
                          return innerAcc
                      }, acc)
                  }, {})
                : {}
            const paidFeatures = {
                isFreePlan: resolvedMetadata?.plan?.toString().toLowerCase() === 'free',
                hasMaterialConsumptionModeOptions:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.material_consumption),
                hasCustomerPricing:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.customer_pricing),
                hasAdvancedMaterials:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.materials_revamp),
                hasCustomerSuppliedMaterial:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.customer_supplied),
                hasUnfoldTube: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.tube),
                hasPdfUpload: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.pdf),
                hasPartLibrary: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.part_library),
                hasWebStore: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.webstore),
                has3d:
                    isOnTrialValue ||
                    (payload?.hasActiveSubscription && resolvedMetadata ? resolvedMetadata['3d'] : false),
                hasMiscellaneousItems:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.miscellaneous_items),
                hasFixedPriceParts:
                    isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.fixed_price_parts),
                hasShipping: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.shipping),
                hasXero: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.xero),
                hasQuickbooks: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.quickbooks),
                hasZapier: isOnTrialValue || (payload?.hasActiveSubscription && resolvedMetadata?.zapier),
                paymentsFee: resolvedMetadata?.payments_fee,
                paymentsFeeInternal: resolvedMetadata?.payments_fee_internal,
            }

            const useImperialUnits =
                payload?.defaultDrawingUnits && payload?.defaultDrawingUnits?.toLowerCase() === 'imperial'

            const currencySymbol = getCurrencySymbol(payload?.currencyCode, payload?.locale)

            // Calculate maximum users using dual-logic (metadata max_users OR largest quantity)
            const maxUsers = calculateMaxUsers(payload?.subscriptions, resolvedMetadata)

            state.planMetadata = metadata
            state.paidFeatures = paidFeatures
            state.useImperialUnits = useImperialUnits
            state.currencySymbol = currencySymbol
            state.currencyCode = payload.currencyCode
            state.isOnTrial = isOnTrialValue
            state.isOnFreePlan = isOnFreePlan
            state.organisationId = payload.organisationId
            state.organisation = payload
            state.hasActiveSubscription = payload.hasActiveSubscription
            state.showAccelerationSettings = payload.showAccelerationSettings
            state.useTubeV2 = payload.useTubeV2
            state.maxUsers = maxUsers
        },
        setLastPaymentStatus: (state, action) => {
            state.lastPaymentStatus = action.payload
        },
        setPaidFeatures: (state, action) => {
            state.paidFeatures = action.payload
        },
        setUseImperialUnits: (state, action) => {
            state.useImperialUnits = action.payload
        },
        setCurrencySymbol: (state, action) => {
            state.currencySymbol = action.payload
        },
        setcurrencyCode: (state, action) => {
            state.currencyCode = action.payload
        },
        setIsOnTrial: (state, action) => {
            state.isOnTrial = action.payload
        },
        setDefaultTaxRateId: (state, action) => {
            state.defaultTaxRateId = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            isAnyOf(
                // Query endpoints
                organisationApi.endpoints.getOrganisation.matchFulfilled,
                // Mutation endpoints - all return complete organisation object
                // Note: transferOwnership is excluded because it returns 204 No Content (null payload).
                // Cache invalidation via invalidatesTags: ['Organisation'] handles the refetch.
                organisationApi.endpoints.updateOrganisation.matchFulfilled,
                organisationApi.endpoints.uploadOrganisationLogo.matchFulfilled,
                organisationApi.endpoints.changeDrawingUnits.matchFulfilled,
                organisationApi.endpoints.toggleLegacyMaterials.matchFulfilled,
                organisationApi.endpoints.importOrganisation.matchFulfilled,
                organisationApi.endpoints.reinitializeOrganisation.matchFulfilled,
                organisationApi.endpoints.uploadStoreLogo.matchFulfilled,
                organisationApi.endpoints.uploadStoreFavicon.matchFulfilled,
                organisationApi.endpoints.fetchXeroInfo.matchFulfilled
            ),
            (state, action) => {
                organisationSlice.caseReducers.setOrganisation(state, action)
            }
        )
    },
})

export const selectOrganisation = (state) => state.organisationSlice.organisation
export const selectOrganisationId = (state) => state.organisationSlice.organisationId
export const selectPlanMetadata = (state) => state.organisationSlice.planMetadata
export const selectLastPaymentStatus = (state) => state.organisationSlice.lastPaymentStatus
export const selectUseImperialUnits = (state) => state.organisationSlice.useImperialUnits
export const selectCurrencySymbol = (state) => state.organisationSlice.currencySymbol
export const selectCurrencyCode = (state) => state.organisationSlice.currencyCode
export const selectPaidFeatures = (state) => state.organisationSlice.paidFeatures
export const selectIsOnTrial = (state) => state.organisationSlice.isOnTrial
export const selectIsOnFreePlan = (state) => state.organisationSlice.isOnFreePlan
export const selectShowAccelerationSettings = (state) => state.organisationSlice.showAccelerationSettings
export const selectDefaultTaxRateId = (state) => state.organisationSlice.defaultTaxRateId
export const selectMaxUsers = (state) => state.organisationSlice.maxUsers ?? 1
export const selectHasActiveSubscription = (state) => state.organisationSlice.hasActiveSubscription
export const selectUseTubeV2 = (state) => state.organisationSlice.useTubeV2
export const selectHasAdvancedMaterials = (state) => state.organisationSlice.paidFeatures?.hasAdvancedMaterials ?? false
export const selectHasUnfoldTube = (state) => state.organisationSlice.paidFeatures?.hasUnfoldTube ?? false
export const selectPaymentsFee = (state) => state.organisationSlice.paidFeatures?.paymentsFee
export const selectPaymentsFeeInternal = (state) => state.organisationSlice.paidFeatures?.paymentsFeeInternal

export const {
    setCurrencySymbol,
    setDefaultTaxRateId,
    setIsOnTrial,
    setLastPaymentStatus,
    setOrganisation,
    setPaidFeatures,
    setUseImperialUnits,
} = organisationSlice.actions

export default organisationSlice.reducer
